import { arrayValue, isArray } from '../array.js';
import type * as Core from '../../types/calculation-core.js';
import { failure, isFiniteNumber, resolveMetadata, success, validateMetadata, } from './types.js';
const BMR_SOURCES = Object.freeze([
    {
        id: 'mifflin-1990-pmid-2305711',
        title: 'A new predictive equation for resting energy expenditure in healthy individuals',
        publisher: 'The American Journal of Clinical Nutrition / PubMed',
        url: 'https://pubmed.ncbi.nlm.nih.gov/2305711/',
        verifiedAt: '2026-08-20',
    },
    {
        id: 'roza-shizgal-1984-pmid-6741850',
        title: 'The Harris Benedict equation reevaluated',
        publisher: 'The American Journal of Clinical Nutrition / PubMed',
        url: 'https://pubmed.ncbi.nlm.nih.gov/6741850/',
        verifiedAt: '2026-08-20',
    },
]);
export const BMR_NOTES = Object.freeze([
    'Formüller dinlenme enerji harcamasını tahmin eder; ölçüm veya tıbbi tanı değildir.',
    'Mifflin–St Jeor çalışması 19–78 yaş katılımcıları kapsar; bu aralığın dışındaki sonuç ekstrapolasyon olarak etiketlenir.',
    'Roza–Shizgal PubMed özetinde kesin alt ve üst yaş sınırı verilmediğinden seçili Harris sonucu bu belirsizlikle etiketlenir.',
    'Yetişkin kapsamı ve metodoloji yılda bir kaynak incelemesinden geçirilmelidir.',
]);
const IDEAL_WEIGHT_SOURCES = Object.freeze([{
        id: 'peterson-2016-pmc4841935',
        title: 'Universal equation for estimating ideal body weight and body weight at any BMI',
        publisher: 'The American Journal of Clinical Nutrition / PubMed Central',
        url: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC4841935/',
        verifiedAt: '2026-08-20',
    }]);
export const IDEAL_WEIGHT_NOTES = Object.freeze([
    'Devine, Robinson ve Hamwi çıktıları tarihsel referans formülleridir; kişisel hedef, tanı veya tedavi önerisi değildir.',
    'Formüller beş fitin altındaki boylarda doğrulanmış klinik hedef olarak yorumlanmamalıdır.',
    'Metodoloji ve kapsam yılda bir kaynak incelemesinden geçirilmelidir.',
]);
function healthMetadata(calculatorId: string, sources: Core.CoreMetadata["sources"], notes: readonly string[]): Core.CoreMetadata {
    return {
        calculatorId,
        version: '1.0.0',
        effectiveFrom: '2026-08-20',
        lastVerifiedAt: '2026-08-20',
        sources,
        notes,
    };
}
function boundedError(field: string, value: unknown, minimum: number, maximum: number) {
    if (typeof value !== 'number' || !isFiniteNumber(value)) {
        return { field, code: 'NOT_FINITE', message: `${field} sonlu bir sayı olmalıdır.` };
    }
    if (value < minimum || value > maximum) {
        return { field, code: 'OUT_OF_RANGE', message: `${field} izin verilen aralığın dışındadır.` };
    }
    return undefined;
}
export function finiteHealthResult<T>(value: T, numbers: readonly number[], metadata: Core.CoreMetadata) {
    return numbers.every(Number.isFinite)
        ? success(value, metadata)
        : failure([{
                field: 'result',
                code: 'CALCULATION_OVERFLOW',
                message: 'Sağlık formülü sonucu sonlu sayı aralığını aştı.',
            }], metadata);
}
function sexError(value: unknown) {
    return value === 'erkek' || value === 'kadin'
        ? undefined
        : { field: 'sex', code: 'UNSUPPORTED_VALUE', message: 'Cinsiyet seçimi desteklenmiyor.' };
}
export const DEFAULT_BMR_CONFIG: Core.BmrConfig = {
    metadata: healthMetadata('basal-metabolic-rate', BMR_SOURCES, BMR_NOTES),
    ageYears: [18, 100],
    heightCm: [100, 250],
    weightKg: [20, 300],
};
function rangeConfigError(field: string, value: unknown, allowedMinimum: number, allowedMaximum: number) {
    const entries = arrayValue(value);
    const range = entries !== null && entries.length === 2 ? entries : null;
    const minimum = range?.[0];
    const maximum = typeof minimum === 'number' && isFiniteNumber(minimum) ? range?.[1] : undefined;
    return typeof minimum === 'number' && isFiniteNumber(minimum)
        && typeof maximum === 'number' && isFiniteNumber(maximum)
        && minimum <= maximum
        && minimum >= allowedMinimum
        && maximum <= allowedMaximum
        ? undefined
        : { field, code: 'INVALID_CONFIG', message: 'Sağlık girdisi sınırı geçersiz.' };
}
function healthMetadataErrors(metadata: Core.CoreMetadata, calculatorId: string, requiredSources: Core.CoreMetadata["sources"], requiredNotes: readonly string[]): Core.InputError[] {
    const errors = [];
    if (metadata?.calculatorId !== calculatorId) {
        errors.push({ field: 'config.metadata.calculatorId', code: 'INVALID_CONFIG', message: 'Sağlık formülü kimliği değiştirilemez.' });
    }
    if (!isArray(metadata?.sources)
        || metadata.sources.length !== requiredSources.length
        || metadata.sources.some((source: Core.CoreMetadata['sources'][number], index) => {
            const required = requiredSources[index];
            return !required
                || source?.id !== required.id
                || source?.title !== required.title
                || source?.publisher !== required.publisher
                || source?.url !== required.url
                || source?.verifiedAt !== required.verifiedAt;
        })) {
        errors.push({ field: 'config.metadata.sources', code: 'INVALID_CONFIG', message: 'Zorunlu metodoloji kaynak kimliği ve yayın bilgisi değiştirilemez.' });
    }
    const notes = metadata?.notes;
    if (notes === undefined || !isArray(notes)
        || notes.length !== requiredNotes.length
        || notes.some((note: string, index) => note !== requiredNotes[index])) {
        errors.push({ field: 'config.metadata.notes', code: 'INVALID_CONFIG', message: 'Sağlık kapsamı ve uyarıları eksik bırakılamaz.' });
    }
    const requiredVerificationDates = requiredSources
        .map((source) => source.verifiedAt)
        .filter((value) => typeof value === 'string')
        .sort();
    const latestRequiredVerification = requiredVerificationDates[requiredVerificationDates.length - 1];
    if (latestRequiredVerification !== undefined
        && metadata?.lastVerifiedAt !== latestRequiredVerification) {
        errors.push({ field: 'config.metadata.lastVerifiedAt', code: 'INVALID_CONFIG', message: 'Metadata doğrulama tarihi kaynak doğrulama kaydıyla eşleşmelidir.' });
    }
    return errors;
}
export function bmrConfigErrors(config: Core.BmrConfig): Core.InputError[] {
    const errors = [
        ...validateMetadata(config.metadata),
        ...healthMetadataErrors(config.metadata, 'basal-metabolic-rate', BMR_SOURCES, BMR_NOTES),
    ];
    const supportedBounds = {
        ageYears: [18, 100],
        heightCm: [100, 250],
        weightKg: [20, 300],
    };
    for (const field of ['ageYears', 'heightCm', 'weightKg'] as const) {
        const error = rangeConfigError(`config.${field}`, config[field], supportedBounds[field][0], supportedBounds[field][1]);
        if (error)
            errors.push(error);
    }
    return errors;
}
export function calculateBmr(input: Core.BmrInput, config: Core.BmrConfig = DEFAULT_BMR_CONFIG) {
    const metadata = resolveMetadata(config?.metadata, DEFAULT_BMR_CONFIG.metadata);
    if (!config || typeof config !== 'object') {
        return failure([{ field: 'config', code: 'INVALID_CONFIG', message: 'BMR konfigürasyonu geçersiz.' }], metadata);
    }
    const configErrors = bmrConfigErrors(config);
    if (configErrors.length > 0)
        return failure(configErrors, metadata);
    if (!input || typeof input !== 'object') {
        return failure([{ field: 'input', code: 'REQUIRED', message: 'BMR girdileri gereklidir.' }], metadata);
    }
    const errors = [];
    const invalidSex = sexError(input.sex);
    const ageError = boundedError('ageYears', input.ageYears, ...config.ageYears);
    const heightError = boundedError('heightCm', input.heightCm, ...config.heightCm);
    const weightError = boundedError('weightKg', input.weightKg, ...config.weightKg);
    if (invalidSex)
        errors.push(invalidSex);
    if (ageError)
        errors.push(ageError);
    if (heightError)
        errors.push(heightError);
    if (weightError)
        errors.push(weightError);
    if (input.formula !== 'mifflin' && input.formula !== 'harris') {
        errors.push({ field: 'formula', code: 'UNSUPPORTED_VALUE', message: 'BMR formülü desteklenmiyor.' });
    }
    if (errors.length > 0)
        return failure(errors, metadata);
    const sexConstant = input.sex === 'erkek' ? 5 : -161;
    const mifflinKcalPerDay = Math.max(0, 10 * input.weightKg + 6.25 * input.heightCm - 5 * input.ageYears + sexConstant);
    const revisedHarrisBenedictKcalPerDay = Math.max(0, input.sex === 'erkek'
        ? 88.362 + 13.397 * input.weightKg + 4.799 * input.heightCm - 5.677 * input.ageYears
        : 447.593 + 9.247 * input.weightKg + 3.098 * input.heightCm - 4.330 * input.ageYears);
    const selectedKcalPerDay = input.formula === 'mifflin'
        ? mifflinKcalPerDay
        : revisedHarrisBenedictKcalPerDay;
    const selectedKcalPerHour = selectedKcalPerDay / 24;
    const selectedSourceScope = input.formula === 'mifflin'
        ? (input.ageYears >= 19 && input.ageYears <= 78
            ? 'within-cited-age-range'
            : 'outside-cited-age-range')
        : 'cited-age-range-not-explicit';
    const selectedSourceScopeNote = input.formula === 'mifflin'
        ? (selectedSourceScope === 'within-cited-age-range'
            ? 'Seçilen yaş, Mifflin–St Jeor çalışmasında bildirilen 19–78 yaş aralığındadır.'
            : 'Seçilen yaş, Mifflin–St Jeor çalışmasında bildirilen 19–78 yaş aralığının dışındadır; sonuç ekstrapolasyondur.')
        : 'Roza–Shizgal PubMed özetinde kesin alt ve üst yaş sınırı verilmediği için yaş kapsamı ayrıca doğrulanamamıştır.';
    return finiteHealthResult({
        ...input,
        mifflinKcalPerDay,
        revisedHarrisBenedictKcalPerDay,
        selectedKcalPerDay,
        selectedKcalPerHour,
        selectedSourceScope,
        selectedSourceScopeNote,
    }, [mifflinKcalPerDay, revisedHarrisBenedictKcalPerDay, selectedKcalPerDay, selectedKcalPerHour], metadata);
}
export const DEFAULT_IDEAL_WEIGHT_CONFIG: Core.IdealWeightConfig = {
    metadata: healthMetadata('historical-ideal-weight-reference', IDEAL_WEIGHT_SOURCES, IDEAL_WEIGHT_NOTES),
    heightCm: [140, 220],
    optionalCurrentWeightKg: [30, 250],
};
export function idealWeightConfigErrors(config: Core.IdealWeightConfig): Core.InputError[] {
    const errors = [
        ...validateMetadata(config.metadata),
        ...healthMetadataErrors(config.metadata, 'historical-ideal-weight-reference', IDEAL_WEIGHT_SOURCES, IDEAL_WEIGHT_NOTES),
    ];
    const heightError = rangeConfigError('config.heightCm', config.heightCm, 140, 220);
    const weightError = rangeConfigError('config.optionalCurrentWeightKg', config.optionalCurrentWeightKg, 30, 250);
    if (heightError)
        errors.push(heightError);
    if (weightError)
        errors.push(weightError);
    return errors;
}
export function calculateIdealWeightReference(input: Core.IdealWeightInput, config: Core.IdealWeightConfig = DEFAULT_IDEAL_WEIGHT_CONFIG) {
    const metadata = resolveMetadata(config?.metadata, DEFAULT_IDEAL_WEIGHT_CONFIG.metadata);
    if (!config || typeof config !== 'object') {
        return failure([{ field: 'config', code: 'INVALID_CONFIG', message: 'İdeal kilo referans konfigürasyonu geçersiz.' }], metadata);
    }
    const configErrors = idealWeightConfigErrors(config);
    if (configErrors.length > 0)
        return failure(configErrors, metadata);
    if (!input || typeof input !== 'object') {
        return failure([{ field: 'input', code: 'REQUIRED', message: 'Boy ve cinsiyet girdileri gereklidir.' }], metadata);
    }
    const errors = [];
    const invalidSex = sexError(input.sex);
    const heightError = boundedError('heightCm', input.heightCm, ...config.heightCm);
    if (invalidSex)
        errors.push(invalidSex);
    if (heightError)
        errors.push(heightError);
    if (input.currentWeightKg !== undefined) {
        const weightError = boundedError('currentWeightKg', input.currentWeightKg, ...config.optionalCurrentWeightKg);
        if (weightError)
            errors.push(weightError);
    }
    if (errors.length > 0)
        return failure(errors, metadata);
    const inchesOverFiveFeet = input.heightCm / 2.54 - 60;
    const devineKg = Math.max(0, (input.sex === 'erkek' ? 50 : 45.5) + 2.3 * inchesOverFiveFeet);
    const robinsonKg = Math.max(0, (input.sex === 'erkek' ? 52 : 49) + (input.sex === 'erkek' ? 1.9 : 1.7) * inchesOverFiveFeet);
    const hamwiKg = Math.max(0, (input.sex === 'erkek' ? 48 : 45.5) + (input.sex === 'erkek' ? 2.7 : 2.2) * inchesOverFiveFeet);
    const referenceMinimumKg = Math.min(devineKg, robinsonKg, hamwiKg);
    const referenceMaximumKg = Math.max(devineKg, robinsonKg, hamwiKg);
    const currentWeightKg = input.currentWeightKg ?? null;
    const heightSourceScope = input.heightCm >= 152.4
        ? 'within-five-foot-reference-basis'
        : 'below-five-foot-extrapolation';
    const heightSourceScopeNote = heightSourceScope === 'within-five-foot-reference-basis'
        ? 'Boy, tarihsel formüllerin 5 fit (152,4 cm) başlangıç noktasındadır veya üzerindedir.'
        : 'Boy, tarihsel formüllerin 5 fit (152,4 cm) başlangıç noktasının altındadır; sonuç geriye doğru ekstrapolasyondur.';
    let currentPosition = null;
    let differenceFromRangeKg = null;
    if (currentWeightKg !== null) {
        if (currentWeightKg < referenceMinimumKg) {
            currentPosition = 'below';
            differenceFromRangeKg = referenceMinimumKg - currentWeightKg;
        }
        else if (currentWeightKg > referenceMaximumKg) {
            currentPosition = 'above';
            differenceFromRangeKg = currentWeightKg - referenceMaximumKg;
        }
        else {
            currentPosition = 'within';
            differenceFromRangeKg = 0;
        }
    }
    return finiteHealthResult({
        sex: input.sex,
        heightCm: input.heightCm,
        currentWeightKg,
        devineKg,
        robinsonKg,
        hamwiKg,
        referenceMinimumKg,
        referenceMaximumKg,
        currentPosition,
        differenceFromRangeKg,
        heightSourceScope,
        heightSourceScopeNote,
    }, [
        devineKg,
        robinsonKg,
        hamwiKg,
        referenceMinimumKg,
        referenceMaximumKg,
        ...(differenceFromRangeKg === null ? [] : [differenceFromRangeKg]),
    ], metadata);
}
